import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Drives the global overlay (`<app-loader>`).
 *
 * The overlay is a full-screen grey wash, so it must never flash on the normal
 * case: a page's own API calls answer in ~200–300ms, but showing the overlay for
 * that half second on every navigation is exactly what made the panel feel slow.
 * It now waits `SHOW_DELAY_MS` before appearing — a request that finishes sooner
 * than that never shows it at all — while a genuinely slow one still gets the
 * spinner. Requests are counted, so parallel calls show it once and the last one
 * to finish hides it.
 */
@Injectable({ providedIn: 'root' })
export class LoaderService {
  /** How long a request may run before the overlay is worth showing. */
  private static readonly SHOW_DELAY_MS = 400;

  private loaderSubject = new BehaviorSubject<boolean>(false);
  loaderState$ = this.loaderSubject.asObservable();

  private inFlight = 0;
  private showTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * `immediate` skips the delay — for a save the admin just pressed, where the overlay is the
   * feedback that the click registered and must not wait to see whether the call is slow.
   */
  show(immediate = false): void {
    this.inFlight++;

    if (immediate) {
      if (this.showTimer) {
        clearTimeout(this.showTimer);
        this.showTimer = null;
      }
      if (!this.loaderSubject.value) this.loaderSubject.next(true);
      return;
    }

    if (this.inFlight === 1 && !this.loaderSubject.value && !this.showTimer) {
      this.showTimer = setTimeout(() => {
        this.showTimer = null;
        // Every request already came back while we were waiting.
        if (this.inFlight > 0) {
          this.loaderSubject.next(true);
        }
      }, LoaderService.SHOW_DELAY_MS);
    }
  }

  hide(): void {
    this.inFlight = Math.max(0, this.inFlight - 1);
    if (this.inFlight > 0) return;

    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
    if (this.loaderSubject.value) {
      this.loaderSubject.next(false);
    }
  }
}
